import React, { useEffect, useRef } from 'react';

import { useForm, useWatch } from 'react-hook-form';

import { Box, FormHelperText, FormLabel, Stack, TextField } from '@mui/material';

import AIGeneratorButton from '~/components/AIGenerator/AIGenerateButton';
import { RichTextEditor } from '~/components/Common/Forms/fields';

import { normalizeRichTextValue } from '../../DefaultContent/utils';
import CopyRoomAttributeButton from '../CopyRoomAttributeButton';
import type { OfferBuilderRoom, RoomWithKey } from '../types';

interface Props {
  onChange: (nextRoomValues: Partial<OfferBuilderRoom>) => void;
  propertyName: string;
  room: OfferBuilderRoom;
  roomKey: string;
  rooms: Array<RoomWithKey>;
}

interface RoomDetailsFormValues {
  description: string;
}

const AI_BUTTON_SX = {
  m: 0,
  maxWidth: 'none',
  p: 0,
  '&.MuiContainer-root': {
    px: 0,
  },
  '& .MuiGridLegacy-container, & .MuiGrid-container': {
    m: 0,
    width: 'auto',
  },
  '& .MuiGridLegacy-item, & .MuiGrid-item': {
    p: 0,
  },
};

export default function DetailsTab({ onChange, propertyName, room, roomKey, rooms }: Props) {
  const { control, setValue } = useForm<RoomDetailsFormValues>({
    defaultValues: {
      description: room.description || '',
    },
  });
  const descriptionValue = useWatch({ control, name: 'description' }) ?? '';
  const previousRoomDescriptionRef = useRef(room.description || '');
  const isDescriptionMissing = !descriptionValue.trim();
  const description = descriptionValue.trim() ? descriptionValue : undefined;
  const amenities = room.amenities
    ?.map((amenity) => (typeof amenity === 'string' ? amenity : amenity.name))
    .filter((amenity): amenity is string => !!amenity);
  const aiContext = {
    model: 'room-type',
    field: 'description',
    propertyName,
    roomName: room.name || '',
    amenities,
  };

  useEffect(() => {
    const roomDescription = room.description || '';

    if (previousRoomDescriptionRef.current !== roomDescription) {
      return;
    }

    if (
      descriptionValue !== roomDescription &&
      normalizeRichTextValue(descriptionValue) !== normalizeRichTextValue(roomDescription)
    ) {
      onChange({ description: descriptionValue });
    }
  }, [descriptionValue, onChange, room.description]);

  useEffect(() => {
    const nextDescription = room.description || '';

    if (previousRoomDescriptionRef.current !== nextDescription) {
      previousRoomDescriptionRef.current = nextDescription;

      if (descriptionValue !== nextDescription) {
        setValue('description', nextDescription);
      }
    }
  }, [descriptionValue, room.description, setValue]);

  return (
    <Stack spacing={3}>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: '1fr',
          gap: 2,
        }}
      >
        <Stack spacing={1}>
          <FormLabel required>Name</FormLabel>
          <TextField
            value={room.name || ''}
            size="small"
            fullWidth
            required
            error={!room.name?.trim()}
            helperText={!room.name?.trim() ? 'Name is required' : undefined}
            slotProps={{ formHelperText: { sx: { ml: 0 } } }}
            onChange={(event) => onChange({ name: event.target.value })}
          />
        </Stack>
      </Box>

      <Stack spacing={1}>
        <Box
          sx={{
            '& .rdw-editor-main': { minHeight: 180 },
            '& [data-testid="description-editor"] > div > .MuiBox-root': {
              borderColor: isDescriptionMissing ? 'error.main' : 'divider',
            },
          }}
        >
          <RichTextEditor
            control={control}
            label="Description"
            name="description"
            required
            error={isDescriptionMissing}
          />
        </Box>
        {isDescriptionMissing && <FormHelperText error>Description is required</FormHelperText>}
        <Stack spacing={1.5} alignItems="flex-start">
          <CopyRoomAttributeButton
            attribute="description"
            roomKey={roomKey}
            rooms={rooms}
            onCopy={(sourceRoom) => onChange({ description: sourceRoom.description || '' })}
          />
          <AIGeneratorButton
            context={aiContext}
            content={description}
            buttonLabel={description ? 'Rewrite with AI' : 'Draft with AI'}
            updateContext={() => aiContext}
            onChoose={(description) => onChange({ description })}
            sx={AI_BUTTON_SX}
          />
        </Stack>
      </Stack>
    </Stack>
  );
}
